import { useState } from "react";
import { CalendarIcon } from "lucide-react";
import { format, startOfMonth, startOfYear, subDays, subMonths, endOfMonth } from "date-fns";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export type PresetKey =
  | "today"
  | "yesterday"
  | "last7"
  | "last30"
  | "last90"
  | "this-month"
  | "last-month"
  | "ytd";

export type DateRange = {
  from: Date;
  to: Date;
  preset?: PresetKey;
};

type Preset = { key: PresetKey; label: string; resolve: (now: Date) => DateRange };

const startOfDay = (d: Date) => {
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  return c;
};
const endOfDay = (d: Date) => {
  const c = new Date(d);
  c.setHours(23, 59, 59, 999);
  return c;
};

export const DEFAULT_PRESETS: Preset[] = [
  { key: "today", label: "Today", resolve: (now) => ({ from: startOfDay(now), to: endOfDay(now), preset: "today" }) },
  { key: "yesterday", label: "Yesterday", resolve: (now) => {
    const y = subDays(now, 1);
    return { from: startOfDay(y), to: endOfDay(y), preset: "yesterday" };
  }},
  { key: "last7", label: "Last 7 days", resolve: (now) => ({ from: startOfDay(subDays(now, 6)), to: endOfDay(now), preset: "last7" }) },
  { key: "last30", label: "Last 30 days", resolve: (now) => ({ from: startOfDay(subDays(now, 29)), to: endOfDay(now), preset: "last30" }) },
  { key: "last90", label: "Last 90 days", resolve: (now) => ({ from: startOfDay(subDays(now, 89)), to: endOfDay(now), preset: "last90" }) },
  { key: "this-month", label: "This month", resolve: (now) => ({ from: startOfMonth(now), to: endOfDay(now), preset: "this-month" }) },
  { key: "last-month", label: "Last month", resolve: (now) => {
    const lastMonth = subMonths(now, 1);
    return { from: startOfMonth(lastMonth), to: endOfMonth(lastMonth), preset: "last-month" };
  }},
  { key: "ytd", label: "Year to date", resolve: (now) => ({ from: startOfYear(now), to: endOfDay(now), preset: "ytd" }) },
];

/**
 * Default — Last 30 days, returned eagerly so consumers can use it as
 * the initial value of a `useState`.
 */
export function defaultRange(): DateRange {
  return DEFAULT_PRESETS.find((p) => p.key === "last30")!.resolve(new Date());
}

/**
 * Enterprise-grade date range picker. Trigger is a button rendering
 * the active preset label or the `Apr 1 – Apr 30, 2026` range; click
 * opens a popover with a preset list on the left + a 2-month
 * calendar on the right. Picking a preset closes immediately;
 * picking a custom range requires Apply.
 */
export function DateRangePicker({
  value,
  onChange,
  presets = DEFAULT_PRESETS,
  align = "start",
}: {
  value: DateRange;
  onChange: (next: DateRange) => void;
  presets?: Preset[];
  align?: "start" | "center" | "end";
}) {
  const [open, setOpen] = useState(false);
  // Local draft for the calendar — only commits to `value` on Apply.
  const [draft, setDraft] = useState<{ from?: Date; to?: Date }>({
    from: value.from,
    to: value.to,
  });

  const onPickPreset = (p: Preset) => {
    const r = p.resolve(new Date());
    setDraft({ from: r.from, to: r.to });
    onChange(r);
    setOpen(false);
  };

  const applyCustom = () => {
    if (!draft.from || !draft.to) return;
    const from = startOfDay(draft.from);
    const to = endOfDay(draft.to);
    onChange({ from, to, preset: undefined });
    setOpen(false);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (next) {
          // Re-sync draft on open so reverting works.
          setDraft({ from: value.from, to: value.to });
        }
        setOpen(next);
      }}
    >
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2 min-w-[180px] justify-start">
          <CalendarIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span className="text-sm">{labelFor(value, presets)}</span>
        </Button>
      </PopoverTrigger>

      <PopoverContent
        side="bottom"
        align={align}
        sideOffset={4}
        className="w-auto p-0"
      >
        <div className="flex">
          <div className="border-r border-border p-2 w-44">
            <p className="px-2 pt-1.5 pb-2 text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
              Quick select
            </p>
            {presets.map((p) => {
              const isActive = value.preset === p.key;
              return (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => onPickPreset(p)}
                  className={cn(
                    "w-full text-left text-sm px-2 py-1.5 rounded-sm transition-colors",
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-muted",
                  )}
                >
                  {p.label}
                </button>
              );
            })}
          </div>

          <div className="p-2">
            <Calendar
              mode="range"
              numberOfMonths={2}
              selected={{ from: draft.from, to: draft.to }}
              onSelect={(r) => setDraft({ from: r?.from, to: r?.to })}
              defaultMonth={value.from}
            />
            <div className="flex items-center justify-between gap-3 px-2 pb-2 pt-1 border-t border-border mt-1">
              <p className="text-xs text-muted-foreground">
                {draft.from && draft.to
                  ? `${format(draft.from, "MMM d, yyyy")} – ${format(draft.to, "MMM d, yyyy")}`
                  : "Pick a range"}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setDraft({ from: value.from, to: value.to });
                    setOpen(false);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  disabled={!draft.from || !draft.to}
                  onClick={applyCustom}
                >
                  Apply
                </Button>
              </div>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

/* ------------------------------------------------------------------ */

function labelFor(r: DateRange, presets: Preset[]): string {
  if (r.preset) {
    return presets.find((p) => p.key === r.preset)?.label ?? customLabel(r);
  }
  return customLabel(r);
}

function customLabel(r: DateRange): string {
  const sameYear = r.from.getFullYear() === r.to.getFullYear();
  const sameMonth = sameYear && r.from.getMonth() === r.to.getMonth();
  if (sameMonth && r.from.getDate() === r.to.getDate()) {
    return format(r.from, "MMM d, yyyy");
  }
  return `${format(r.from, "MMM d")} – ${format(r.to, sameYear ? "MMM d, yyyy" : "MMM d, yyyy")}`;
}

/**
 * Wire-format a Date as `YYYY-MM-DD` for the backend's LocalDate
 * params. Use the local date (not UTC) so the user's picked
 * "Apr 30" is sent as "2026-04-30" regardless of timezone.
 */
export function toIsoDate(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}
